// js/views/ceo-cockpit.ts — CEO Kokpit (Lane-1, 2026-07-04).
// Tek karar yüzeyi: Bugün Beni Bekleyen (council+approval, aksiyonlar MEVCUT
// governance endpoint'lerine) + Council SLA + Agent Health + Board Hijyeni +
// Deploy Kapıları. Endpoint: GET /api/v2/ceo-cockpit (flag'li; kapalıysa bilgi kartı).
// Desen: aktif-isler.ts (apiGetJson + apiFetch aksiyon + confirm guard + busy kilidi).

import { escHtml, ago } from '../utils.js';
import { apiGetJson, apiFetch } from '../api.js';

interface WqAction { label: string; method: 'POST'; path: string; body?: Record<string, unknown>; confirm: string }
interface WqItem {
  kind: string; id: string; title: string; owner: string | null; risk: string | null;
  status: string; createdAt: string | null; needsYilmaz: boolean; actions: WqAction[];
}
interface CouncilTodayItem extends WqItem { ageMinutes: number | null; slaBreached: boolean }
interface AgentHealthRow {
  agentId: string; name: string; ok24h: number; fail24h: number;
  livenessState: string | null; livenessReason: string | null;
  lastRunAt: string | null; lastRunStatus: string | null; lastError: string | null;
  health: 'healthy' | 'degraded' | 'failing';
}
interface CockpitResponse {
  ok: true;
  enabled: boolean;
  generatedAt?: string;
  today?: {
    council: CouncilTodayItem[]; approvals: WqItem[];
    hermes: { ownerReview: number; proposeQuarantine: number; proposeSent: number } | null;
    deployPlansPending: number;
  };
  councilSla?: { openCount: number; oldestAgeMinutes: number; breachedCount: number } | null;
  agentHealth?: AgentHealthRow[] | null;
  agentHealthNote?: string;
  board?: {
    blocked: number; blockedStale14d: number; inReview: number;
    topBlocked: Array<{ id: string; identifier: string | null; title: string; blockedDays: number; lastCommentSnippet: string | null }>;
  } | null;
  deployGates?: Array<{ id: string; target: string | null; risk: string | null; createdAt: string | null }> | null;
  // Faz 52-Y W3d — Para paneli (müşteri başına açık fatura).
  finance?: Array<{
    client_slug: string; status: string; cnt: number; total: string | number;
    currency: string; oldest_overdue_days: number | null;
  }> | null;
  errors?: Array<{ domain: string; message: string }>;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'disabled' }
  | { kind: 'ready'; data: CockpitResponse }
  | { kind: 'error'; message: string };

let vs: ViewState = { kind: 'idle' };
let busy = false;

export async function loadCeoCockpit(): Promise<void> {
  vs = { kind: 'loading' };
  const r = await apiGetJson<CockpitResponse>('/api/v2/ceo-cockpit');
  if (!r.ok) {
    vs = { kind: 'error', message: r.error ?? 'yüklenemedi' };
    return;
  }
  vs = r.data.enabled ? { kind: 'ready', data: r.data } : { kind: 'disabled' };
}

async function runAction(a: WqAction): Promise<void> {
  if (busy) return;
  if (!window.confirm(a.confirm)) return;
  busy = true;
  try {
    const res = await apiFetch(`/api/v2${a.path}`, {
      method: a.method,
      headers: { 'Content-Type': 'application/json' },
      body: a.body ? JSON.stringify(a.body) : undefined,
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
      window.alert(`Aksiyon başarısız: ${j.error ?? res.status}${j.message ? ` — ${j.message}` : ''}`);
    }
  } finally {
    busy = false;
    await loadCeoCockpit();
    renderCeoCockpit();
  }
}

// ── küçük yardımcılar (inline, tema-değişkeni + fallback) ────────────────────

const CARD = 'background:var(--panel,#141a26);border:1px solid var(--border,#26304a);border-radius:10px;padding:14px;';
const MUTED = 'color:var(--muted,#8b98b8);font-size:12px;';

function kpi(label: string, value: string | number, tone?: 'red' | 'amber' | 'green'): string {
  const color = tone === 'red' ? '#f0a8a8' : tone === 'amber' ? '#fde047' : tone === 'green' ? '#9be8a8' : 'var(--fg,#e6ecff)';
  return `<div style="${CARD}min-width:130px;flex:1">
    <div style="${MUTED}">${escHtml(label)}</div>
    <div style="font-size:22px;font-weight:700;color:${color}">${escHtml(String(value))}</div>
  </div>`;
}

function riskPill(risk: string | null): string {
  if (!risk) return '';
  const tone = risk === 'critical' || risk === 'high' ? '#f0a8a8' : risk === 'medium' ? '#fde047' : '#9bd0ff';
  return `<span style="border:1px solid ${tone};color:${tone};border-radius:999px;padding:1px 8px;font-size:11px;margin-left:6px">${escHtml(risk)}</span>`;
}

function healthPill(h: AgentHealthRow['health']): string {
  const map = { healthy: ['#9be8a8', 'sağlıklı'], degraded: ['#fde047', 'zayıf'], failing: ['#f0a8a8', 'ARIZALI'] } as const;
  const [tone, label] = map[h];
  return `<span style="border:1px solid ${tone};color:${tone};border-radius:999px;padding:1px 8px;font-size:11px">${label}</span>`;
}

function waitingRow(it: WqItem, extra: string): string {
  const acts = it.actions
    .map((a, i) => `<button type="button" class="cc-act" data-item="${escHtml(it.kind)}:${escHtml(it.id)}" data-idx="${i}"
        style="margin-left:6px;padding:3px 10px;border-radius:6px;border:1px solid var(--border,#26304a);background:var(--panel2,#1b2233);color:var(--fg,#e6ecff);cursor:pointer">${escHtml(a.label)}</button>`)
    .join('');
  return `<div style="${CARD}display:flex;align-items:center;gap:10px;margin-bottom:8px">
    <div style="flex:1;min-width:0">
      <div style="font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(it.title)}${riskPill(it.risk)}${extra}</div>
      <div style="${MUTED}">${escHtml(it.kind)} · ${it.owner ? escHtml(it.owner) + ' · ' : ''}${it.createdAt ? escHtml(ago(it.createdAt)) : '—'}</div>
    </div>
    <div style="white-space:nowrap">${acts}</div>
  </div>`;
}

// ── render ───────────────────────────────────────────────────────────────────

export function renderCeoCockpit(): void {
  const root = document.getElementById('view-ceo-cockpit');
  if (!root) return;

  if (vs.kind === 'loading' || vs.kind === 'idle') {
    root.innerHTML = `<div style="${MUTED}padding:20px">Kokpit yükleniyor…</div>`;
    return;
  }
  if (vs.kind === 'disabled') {
    root.innerHTML = `<div style="${CARD}margin:20px;max-width:520px">
      <h3 style="margin:0 0 6px">🎛️ CEO Kokpit devre dışı</h3>
      <div style="${MUTED}">Sunucuda <code>CEO_COCKPIT_ENABLED=true</code> ayarlanınca bu görünüm aktifleşir.</div>
    </div>`;
    return;
  }
  if (vs.kind === 'error') {
    root.innerHTML = `<div style="${CARD}margin:20px;color:#f0a8a8">Kokpit yüklenemedi: ${escHtml(vs.message)}
      <button type="button" id="cc-retry" style="margin-left:10px">Yenile</button></div>`;
    document.getElementById('cc-retry')?.addEventListener('click', () => { void loadCeoCockpit().then(renderCeoCockpit); });
    return;
  }

  const d = vs.data;
  const t = d.today;
  const sla = d.councilSla;
  const waiting: Array<{ it: WqItem; extra: string }> = [
    ...(t?.council ?? []).map((c) => ({
      it: c as WqItem,
      extra: c.slaBreached
        ? `<span style="background:#f0a8a8;color:#301010;border-radius:999px;padding:1px 8px;font-size:11px;margin-left:6px;font-weight:700">SLA</span>`
        : '',
    })),
    ...(t?.approvals ?? []).map((a) => ({ it: a, extra: '' })),
  ];

  const health = d.agentHealth ?? [];
  const failing = health.filter((h) => h.health !== 'healthy');
  const board = d.board;
  const gates = d.deployGates ?? [];
  const errs = d.errors ?? [];
  // Faz 52-Y W3d — Para: overdue en üstte, gecikme gününe göre sıralı.
  const finance = [...(d.finance ?? [])].sort((a, b) =>
    (b.status === 'overdue' ? 1 : 0) - (a.status === 'overdue' ? 1 : 0) ||
    (b.oldest_overdue_days ?? -1) - (a.oldest_overdue_days ?? -1));
  const overdueTotal = finance
    .filter((f) => f.status === 'overdue')
    .reduce((s, f) => s + (Number(f.total) || 0), 0);
  const fmtTL = (v: number | string, cur: string) =>
    `${Math.round(Number(v) || 0).toLocaleString('tr-TR')} ${cur || 'TRY'}`;

  root.innerHTML = `
  <div style="padding:16px;max-width:1200px">
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
      <h2 style="margin:0">🎛️ CEO Kokpit</h2>
      <span style="${MUTED}">${d.generatedAt ? escHtml(ago(d.generatedAt)) : ''}</span>
      <button type="button" id="cc-refresh" style="margin-left:auto;padding:4px 12px;border-radius:6px;border:1px solid var(--border,#26304a);background:var(--panel2,#1b2233);color:var(--fg,#e6ecff);cursor:pointer">⟳ Yenile</button>
    </div>

    ${errs.length ? `<div style="${CARD}border-color:#fde047;color:#fde047;margin-bottom:12px">⚠ Bazı veriler yüklenemedi: ${errs.map((e) => escHtml(e.domain)).join(', ')}</div>` : ''}

    <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:18px">
      ${kpi('Açık council', sla?.openCount ?? '—', (sla?.openCount ?? 0) > 0 ? 'amber' : 'green')}
      ${kpi('En eski karar', sla ? Math.round(sla.oldestAgeMinutes / 60) + ' saat' : '—', sla && sla.oldestAgeMinutes > 24 * 60 ? 'red' : undefined)}
      ${kpi('SLA ihlali', sla?.breachedCount ?? '—', (sla?.breachedCount ?? 0) > 0 ? 'red' : 'green')}
      ${kpi('Onay bekleyen', t?.approvals.length ?? '—')}
      ${kpi('Deploy kapısı', t?.deployPlansPending ?? '—', (t?.deployPlansPending ?? 0) > 0 ? 'amber' : undefined)}
      ${kpi('Bloklu (bayat 14g+)', board ? `${board.blocked} (${board.blockedStale14d})` : '—', (board?.blockedStale14d ?? 0) > 0 ? 'amber' : undefined)}
      ${kpi('Arızalı ajan', failing.filter((h) => h.health === 'failing').length, failing.some((h) => h.health === 'failing') ? 'red' : 'green')}
      ${kpi('Vadesi geçen', overdueTotal > 0 ? fmtTL(overdueTotal, 'TRY') : 'yok', overdueTotal > 0 ? 'red' : 'green')}
    </div>

    <h3 style="margin:0 0 10px">📌 Bugün Beni Bekleyen <span style="${MUTED}">(${waiting.length})</span></h3>
    <div style="margin-bottom:18px">
      ${waiting.length ? waiting.map((w) => waitingRow(w.it, w.extra)).join('') : `<div style="${MUTED}">Bekleyen karar yok 🎉</div>`}
      ${t?.hermes ? `<div style="${MUTED}margin-top:6px">Hermes owner-review: ${t.hermes.ownerReview} dosya · karantina: ${t.hermes.proposeQuarantine}</div>` : ''}
    </div>

    <h3 style="margin:0 0 10px">💰 Para <span style="${MUTED}">(açık faturalar — Faz 52-Y W3d)</span></h3>
    <div style="${CARD}margin-bottom:18px">
      ${finance.length ? finance.map((f) => `
        <div style="display:flex;gap:10px;padding:5px 0;border-top:1px solid var(--border,#26304a);align-items:center">
          <strong style="min-width:110px">${escHtml(f.client_slug)}</strong>
          <span style="${MUTED}min-width:70px">${escHtml(f.status)}${f.status === 'overdue' && f.oldest_overdue_days ? ` <span style="color:#f0a8a8">(${f.oldest_overdue_days} gün)</span>` : ''}</span>
          <span style="${MUTED}">${f.cnt} fatura</span>
          <strong style="margin-left:auto;color:${f.status === 'overdue' ? '#f0a8a8' : 'inherit'}">${escHtml(fmtTL(f.total, f.currency))}</strong>
        </div>`).join('') : `<div style="${MUTED}">Açık fatura yok 🎉</div>`}
    </div>

    <h3 style="margin:0 0 10px">🤖 Ajan Sağlığı <span style="${MUTED}">(24 saat)</span></h3>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:6px">
      ${health.slice(0, 12).map((h) => `
        <div style="${CARD}min-width:150px">
          <div style="display:flex;justify-content:space-between;gap:8px">
            <strong>${escHtml(h.name)}</strong>${healthPill(h.health)}
          </div>
          <div style="${MUTED}">ok/fail: <span style="color:${h.fail24h > h.ok24h ? '#f0a8a8' : 'inherit'}">${h.ok24h}/${h.fail24h}</span></div>
          ${h.lastError ? `<div style="${MUTED}overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${escHtml(h.lastError)}">${escHtml(h.lastError)}</div>` : ''}
        </div>`).join('') || `<div style="${MUTED}">Veri yok</div>`}
    </div>
    <div style="${MUTED}margin-bottom:18px">${escHtml(d.agentHealthNote ?? '')}</div>

    <h3 style="margin:0 0 10px">🧹 Board Hijyeni</h3>
    <div style="${CARD}margin-bottom:18px">
      ${board ? `
        <div style="${MUTED}margin-bottom:8px">bloklu: <strong>${board.blocked}</strong> · 14g+ bayat: <strong>${board.blockedStale14d}</strong> · in-review: <strong>${board.inReview}</strong></div>
        ${board.topBlocked.map((b) => `
          <div style="display:flex;gap:10px;padding:5px 0;border-top:1px solid var(--border,#26304a)">
            <span style="${MUTED}white-space:nowrap">${escHtml(b.identifier ?? b.id.slice(0, 8))}</span>
            <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(b.title)}</span>
            <span style="${MUTED}white-space:nowrap">${b.blockedDays}g</span>
            <span style="${MUTED}max-width:280px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${escHtml(b.lastCommentSnippet ?? '')}">${escHtml(b.lastCommentSnippet ?? '—')}</span>
          </div>`).join('')}` : `<div style="${MUTED}">Veri yok</div>`}
    </div>

    <h3 style="margin:0 0 10px">🚦 Deploy Kapıları <span style="${MUTED}">(onay: D3 CLI/Telegram)</span></h3>
    <div style="${CARD}">
      ${gates.length ? gates.map((g) => `
        <div style="display:flex;gap:10px;padding:5px 0;border-top:1px solid var(--border,#26304a)">
          <strong style="min-width:110px">${escHtml(g.target ?? '?')}</strong>
          ${riskPill(g.risk)}
          <span style="${MUTED};margin-left:auto">${g.createdAt ? escHtml(ago(g.createdAt)) : '—'}</span>
        </div>`).join('') : `<div style="${MUTED}">Bekleyen deploy planı yok</div>`}
    </div>
  </div>`;

  // Aksiyon bağlama (delegasyonsuz basit binding — kart sayısı küçük).
  document.getElementById('cc-refresh')?.addEventListener('click', () => {
    void loadCeoCockpit().then(renderCeoCockpit);
  });
  const allItems: WqItem[] = [...(t?.council ?? []), ...(t?.approvals ?? [])];
  root.querySelectorAll<HTMLButtonElement>('button.cc-act').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.item ?? '';
      const idx = Number(btn.dataset.idx ?? '-1');
      const item = allItems.find((i) => `${i.kind}:${i.id}` === key);
      const action = item?.actions[idx];
      if (action) void runAction(action);
    });
  });
}
